import { useState, useMemo, useEffect } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import * as z from "zod";
import toast from "react-hot-toast";
import { convertToBackendDateFormat } from "../../../../shared/utils/dateUtils";
import type {
  BuildingOption,
  BuildingOptionsResponse,
  ApiError,
} from "../../../../api/client/types/common.types";
import type { ReportFormat } from "../../../../api/client/types/report.types";
import { buildReportFileName, type ReportType } from "../../utils/reportFilenames";
import { useGetReportTypes } from "../../../../shared/services/catalogService";
import {
  REPORT_TYPE_NAMES,
  findReportTypeByName,
} from "../../../news/utils/reportTypeConstants";

// Custom validation for DD/MM/YYYY format
const ddmmyyyyRegex = /^\d{2}\/\d{2}\/\d{4}$/;

const createReportFormSchema = (maxRangeDays: number) => z.object({
  start_date: z
    .string()
    .min(1, { message: "La fecha de inicio es requerida" })
    .regex(ddmmyyyyRegex, { message: "Formato inválido. Use DD/MM/YYYY" }),
  end_date: z
    .string()
    .min(1, { message: "La fecha de fin es requerida" })
    .regex(ddmmyyyyRegex, { message: "Formato inválido. Use DD/MM/YYYY" }),
  building_id: z.string().optional(),
}).refine(
  (data) => {
    const [startDay, startMonth, startYear] = data.start_date.split("/").map(Number);
    const [endDay, endMonth, endYear] = data.end_date.split("/").map(Number);
    return new Date(startYear, startMonth - 1, startDay) <= new Date(endYear, endMonth - 1, endDay);
  },
  {
    message: "La fecha de inicio debe ser anterior o igual a la fecha de fin",
    path: ["start_date"],
  }
).refine(
  (data) => {
    const [startDay, startMonth, startYear] = data.start_date.split("/").map(Number);
    const [endDay, endMonth, endYear] = data.end_date.split("/").map(Number);
    const diffTime = Math.abs(new Date(endYear, endMonth - 1, endDay).getTime() - new Date(startYear, startMonth - 1, startDay).getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 <= maxRangeDays;
  },
  {
    message: `El rango de fechas no puede exceder ${maxRangeDays} días`,
    path: ["end_date"],
  }
);

interface GetBuildingsRequest {
  start_date: string;
  end_date: string;
  /**
   * Optional comma-separated list of report type IDs to exclude buildings that only
   * have news of these types in the period (e.g., "4" or "2,4").
   * Used only for the news report when generating PDF, to hide buildings that
   * exclusively have "Puesto de P.A / Botiquín" news.
   */
  exclude_report_type_ids?: string;
}

interface UseReportGeneratorProps {
  reportType: ReportType;
  /**
   * Called when the user triggers report generation.
   * Receives the resolved building name so callers can build consistent filenames.
   * Return a `Blob` for file-download formats (e.g. Excel).
   * Return `null` when the export is handled entirely client-side (e.g. PDF via jsPDF),
   * so the hook skips the blob-download step.
   */
  onGenerate: (start_date: string, end_date: string, building_id?: string, format?: ReportFormat, building_name?: string, report_type_id?: number) => Promise<Blob | null>;
  buildingsFetcher?: UseMutationResult<BuildingOptionsResponse, ApiError, GetBuildingsRequest>;
  maxRangeDays?: number;
}

/**
 * Generic custom hook for ReportGenerator business logic
 * Handles:
 * - Form validation with Zod
 * - Date range state management
 * - Building options fetching (generic - works with any mutation)
 * - Report generation and file download
 * - Loading state
 * - 32-day range validation
 */
export const useReportGenerator = ({
  reportType,
  onGenerate,
  buildingsFetcher,
  maxRangeDays = 32,
}: UseReportGeneratorProps) => {
  // Initialize with empty values - user must select dates manually
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [buildingId, setBuildingId] = useState<string>("");
  const [format, setFormat] = useState<ReportFormat>("excel");
  const [reportTypeId, setReportTypeId] = useState<number | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    start_date?: string;
    end_date?: string;
    building_id?: string;
  }>({});

  // Building options state
  const [buildingOptions, setBuildingOptions] = useState<BuildingOption[]>([]);

  // Catalog data for report types (used to avoid hard-coding IDs)
  const { data: reportTypes } = useGetReportTypes();
  const reportFormSchema = useMemo(
    () => createReportFormSchema(maxRangeDays),
    [maxRangeDays],
  );

  // Automatically fetch buildings when both dates are entered and valid
  useEffect(() => {
    if (!startDate || !endDate) {
      // If either date is cleared, reset buildings
      setBuildingOptions([]);
      setBuildingId("");
      return;
    }

    const startBackendFormat = convertToBackendDateFormat(startDate);
    const endBackendFormat = convertToBackendDateFormat(endDate);

    if (!startBackendFormat || !endBackendFormat) {
      setBuildingOptions([]);
      setBuildingId("");
      return;
    }

    // Validate dates before fetching
    const result = reportFormSchema.safeParse({
      start_date: startBackendFormat,
      end_date: endBackendFormat,
    });

    if (!result.success) {
      // If dates are invalid, clear buildings
      setBuildingOptions([]);
      setBuildingId("");
      return;
    }

    if (!buildingsFetcher) return;

    // Build request params.
    // For the NEWS report in PDF format, exclude buildings that only have
    // "Puesto de P.A / Botiquín" news in the range.
    // To avoid hard-coding IDs, we resolve the report type ID by name from the catalog.
    let excludeReportTypeIds: string | undefined;

    if (reportType === "news" && format === "pdf" && reportTypes) {
      const firstAidReportType = findReportTypeByName(
        reportTypes,
        REPORT_TYPE_NAMES.FIRST_AID,
      );

      if (firstAidReportType) {
        excludeReportTypeIds = firstAidReportType.id.toString();
      }
    }

    const requestParams: GetBuildingsRequest = {
      start_date: startBackendFormat,
      end_date: endBackendFormat,
      ...(excludeReportTypeIds
        ? { exclude_report_type_ids: excludeReportTypeIds }
        : {}),
    };

    // Fetch buildings automatically using the provided fetcher
    buildingsFetcher.mutate(requestParams, {
      onSuccess: (data) => {
        setBuildingOptions(data.buildings);
        // Clear building selection if the previously selected building is not in the new list
        const currentBuildingId = buildingId;
        if (currentBuildingId && !data.buildings.some((b) => b.id === currentBuildingId)) {
          setBuildingId("");
        }
      },
      onError: (error) => {
        console.error("Error fetching buildings:", error);
        toast.error("Error al cargar las unidades residenciales");
        setBuildingOptions([]);
        setBuildingId("");
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, format, reportType, maxRangeDays]);

  // Validate form data using Zod
  const validateForm = (data: { start_date: string; end_date: string; building_id?: string }): boolean => {
    const result = reportFormSchema.safeParse(data);
    
    if (!result.success) {
      const newErrors: { start_date?: string; end_date?: string; building_id?: string } = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as string;
        if (field === "start_date" || field === "end_date" || field === "building_id") {
          newErrors[field] = issue.message;
        }
      });
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  // Validate on change
  const handleStartDateChange = (newStartDate: string) => {
    setStartDate(newStartDate);
    // Only validate if we have values to validate
    if (newStartDate) {
      const backendFormat = convertToBackendDateFormat(newStartDate);
      const endBackendFormat = endDate ? convertToBackendDateFormat(endDate) : "";
      if (backendFormat && endBackendFormat) {
        validateForm({ 
          start_date: backendFormat, 
          end_date: endBackendFormat,
          building_id: buildingId || undefined,
        });
      }
    } else {
      // Clear errors when start date is cleared
      setErrors({});
    }
  };

  const handleEndDateChange = (newEndDate: string) => {
    setEndDate(newEndDate);
    // Only validate if we have both dates
    if (startDate && newEndDate) {
      const startBackendFormat = convertToBackendDateFormat(startDate);
      const backendFormat = convertToBackendDateFormat(newEndDate);
      if (startBackendFormat && backendFormat) {
        validateForm({ 
          start_date: startBackendFormat, 
          end_date: backendFormat,
          building_id: buildingId || undefined,
        });
      }
    }
  };

  const handleDateRangeChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    if (!newStartDate && !newEndDate) {
      setErrors({});
      return;
    }

    if (!newStartDate || !newEndDate) {
      setErrors((prev) => ({
        ...prev,
        ...(newStartDate ? {} : { start_date: undefined }),
        ...(newEndDate ? {} : { end_date: undefined }),
      }));
      return;
    }

    const startBackendFormat = convertToBackendDateFormat(newStartDate);
    const endBackendFormat = convertToBackendDateFormat(newEndDate);
    if (startBackendFormat && endBackendFormat) {
      validateForm({
        start_date: startBackendFormat,
        end_date: endBackendFormat,
        building_id: buildingId || undefined,
      });
    }
  };

  const handleBuildingIdChange = (newBuildingId: string) => {
    setBuildingId(newBuildingId);
    // Only validate if we have both dates
    if (startDate && endDate) {
      const startBackendFormat = convertToBackendDateFormat(startDate);
      const endBackendFormat = convertToBackendDateFormat(endDate);
      if (startBackendFormat && endBackendFormat) {
        validateForm({ 
          start_date: startBackendFormat, 
          end_date: endBackendFormat,
          building_id: newBuildingId || undefined,
        });
      }
    }
  };

  const handleFormatChange = (newFormat: ReportFormat) => {
    setFormat(newFormat);
    if (newFormat === "pdf") setReportTypeId(undefined);
  };

  const handleReportTypeChange = (id: number | undefined) => setReportTypeId(id);

  // Check if form is valid
  const isValid = useMemo(() => {
    // Both dates must be selected
    if (!startDate || !endDate) {
      return false;
    }
    
    const startBackendFormat = convertToBackendDateFormat(startDate);
    const endBackendFormat = convertToBackendDateFormat(endDate);
    
    if (!startBackendFormat || !endBackendFormat) {
      return false;
    }
    
    const result = reportFormSchema.safeParse({ 
      start_date: startBackendFormat, 
      end_date: endBackendFormat,
      building_id: buildingId || undefined,
    });
    return result.success;
  }, [startDate, endDate, buildingId, reportFormSchema]);

  // Handle report generation
  const handleGenerateReport = async () => {
    const startBackendFormat = convertToBackendDateFormat(startDate);
    const endBackendFormat = convertToBackendDateFormat(endDate);
    
    // Validate before generating
    if (!validateForm({ 
      start_date: startBackendFormat, 
      end_date: endBackendFormat,
      building_id: buildingId || undefined,
    })) {
      toast.error("Por favor, corrige los errores en el formulario");
      return;
    }

    const selectedBuilding = buildingOptions.find((building) => building.id === buildingId);
    const resolvedBuildingName = selectedBuilding?.name ?? "Todas las unidades residenciales";

    setIsGenerating(true);
    try {
      const blob = await onGenerate(
        startBackendFormat,
        endBackendFormat,
        buildingId || undefined,
        format,
        resolvedBuildingName,
        format === "excel" ? reportTypeId : undefined,
      );

      if (blob !== null) {
        // Server-generated file (e.g. Excel) — trigger browser download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = buildReportFileName({
          reportType,
          buildingName: resolvedBuildingName,
          startDate: startBackendFormat,
          endDate: endBackendFormat,
          extension: format === "pdf" ? "pdf" : "xlsx",
        });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      // When null, the client-side export (e.g. jsPDF) already triggered doc.save()

      toast.success("Reporte generado y descargado correctamente");
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Error al generar el reporte. Por favor, intenta nuevamente.");
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    // State
    startDate,
    endDate,
    buildingId,
    isGenerating,
    errors,
    isValid,
    buildingOptions,
    isFetchingBuildings: buildingsFetcher?.isPending ?? false,
    format,
    reportTypeId,
    reportTypes,
    // Handlers
    handleStartDateChange,
    handleEndDateChange,
    handleDateRangeChange,
    handleBuildingIdChange,
    handleFormatChange,
    handleReportTypeChange,
    handleGenerateReport,
  };
};

